<template>
  <layout :backgroundImageUrl="backgroundImageUrl" v-statusbar.hide>
    <div class="interaction-button">
      <interaction-mode-button
        :isInInteractiveMode="isGovernmentInInteractiveMode"
        :disabled="!currentLevelHasGovernmentData"
        :class="{
          'animate-breathe':
            !isGovernmentInInteractiveMode && currentLevelHasGovernmentData
        }"
      />
    </div>
    <div class="settings-button">
      <settings-menu :returningToMenu="showMenu" />
    </div>
    <content-scroll-container
      :scroll="false"
      :overflow-visible="true"
      class="content-scroll-container"
    >
      <content-scroll-inner class="h-full flex flex-col justify-center">
        <government-menu
          @menu-click="onMenuClick"
          @survey-click="openSurvey"
          :informativeContent="
            isGovernmentInInteractiveMode
              ? ''
              : $t(`views.government-home.information.${constituencyLevel}`)
          "
          :informativeContentDisableClick="!isMobileScreen"
          class="w-full"
          :topLabel="
            $t(`views.information.government.${constituencyLevel}.name`)
          "
          :topSubLabel="constituencyName"
          :topLeftLabel="executiveCouncilName"
          :topRightLabel="judiciaryCouncilName"
          :middleLabel="legislativeCouncilName"
          :bottomLeftLabel="legislativeLeftChamber"
          :bottomRightLabel="legislativeRightChamber"
          :bottomLabel="$t('views.government-home.people')"
          :flagUrl="flagUrl[constituencyLevel]"
          :constituencyLevel="constituencyLevel"
          :levelSwitchBus="levelSwitchBus"
          :boldTopLabel="constituencyLevel !== 'federal'"
        />
      </content-scroll-inner>
    </content-scroll-container>
    <template v-slot:left>
      <InformationText
        :title="$t(`views.information.government.${constituencyLevel}.title`)"
        :subtitle="
          $t(`views.information.government.${constituencyLevel}.subtitle`)
        "
        :content="
          $t(`views.information.government.${constituencyLevel}.content`)
        "
        :link="
          $t(`views.information.government.${constituencyLevel}.button.link`)
        "
        :linkLabel="
          $t(`views.information.government.${constituencyLevel}.button.label`)
        "
        containerClass="h-screen w-full bg-white text-left p-20 text-color-candidate text-chplus-l flex flex-col gap-8 overflow-y-auto"
      />
    </template>
    <template v-slot:bottom>
      <home-navigation
        onGovPage
        :electionNotification="electionNotification"
        :activeSurvey="activeSurvey"
        :canAnswerSurvey="canAnswerSurvey"
        @survey-click="openSurvey"
      >
      </home-navigation>
    </template>
    <survey
      v-if="activeSurvey != null"
      :show="showSurvey"
      :survey-id="activeSurvey.id"
      class="absolute top-0 left-0 w-full h-full"
      :levelSwitchBus="levelSwitchBus"
      ><template v-slot:close-action>
        <button @click="closeSurvey">
          <icon-close class="text-3xl" /></button></template
    ></survey>
    <language-not-available-alert
      v-if="showWarningLanguageNotAvailable"
      :show="showWarningLanguageNotAvailable"
      :selectedContentLanguage="contentLanguage"
      :availableContentLanguage="electionContentLanguage"
      @continue="
        $store.commit('user/setHasSeenLanguageNotAvailableWarning', true)
      "
    />
    <tutorial :show-bus="tutorialBus" />
    <level-switch
      :hasDataAvailableForLevel="levelHasGovernmentData"
      :levelSwitchBus="levelSwitchBus"
      :showNotificationForLevel="showNotificationLevelSwitch"
    />
    <pop-up
      v-if="showAvailabilityPopUp"
      @close="showAvailabilityPopUp = false"
      :title="availabilityPopUpTitle"
    >
      <p class="mt-4">{{ availabilityPopUpText }}</p>
      <div class="flex justify-start gap-2 flex-wrap">
        <a
          v-for="website in availabilityWebsites"
          :key="website"
          :href="website.url"
          target="_blank"
          class="rounded-lg p-3 bg-interaction-dark text-white inline-block"
        >
          {{ website.label }}
          <icon-link-profile />
        </a>
      </div>
    </pop-up>
  </layout>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";

import store from "@/store";
import dayjs from "dayjs";

import client from "api-client";
import IconClose from "@/components/icons/IconCircleReject";
import IconLinkProfile from "@/components/icons/IconLinkProfile";
import InteractionModeButton from "@/components/buttons/InteractionModeButton";
import ContentScrollContainer from "@/components/layouts/ContentScrollContainer";
import ContentScrollInner from "@/components/layouts/ContentScrollInner";
import HomeNavigation from "@/components/layouts/HomeNavigation";
import LanguageNotAvailableAlert from "@/components/layouts/LanguageNotAvailableAlert";
import Layout from "@/components/layouts/LayoutHome";
import Survey from "@/components/layouts/Survey";
import SettingsMenu from "@/components/nav/SettingsMenu";
import GovernmentMenu from "@/components/nav/government/GovernmentMenu";
import Tutorial from "@/components/tutorials/TutorialHome";
import { ElectionProgressStepId } from "@/utils/ElectionProgressStepId";
import { ProgressStepId } from "@/utils/ProgressStepId";
import Vue from "vue";
import backgroundImageUrl from "@/assets/menu/menu-background-government.svg";
import LevelSwitch from "@/components/buttons/LevelSwitch.vue";
import PopUp from "../components/layouts/PopUp.vue";
import OnlineMixin from "@/utils/OnlineMixin";
import StatusBarDirective from "@/directives/StatusBarDirective";
import IsMobileMixin from "@/utils/IsMobileMixin";
import InformationText from "../components/misc/InformationText.vue";

export default {
  name: "GovernmentHome",
  directives: { statusbar: StatusBarDirective },
  components: {
    ContentScrollInner,
    LanguageNotAvailableAlert,
    Survey,
    ContentScrollContainer,
    Layout,
    SettingsMenu,
    IconClose,
    HomeNavigation,
    Tutorial,
    GovernmentMenu,
    InteractionModeButton,
    LevelSwitch,
    PopUp,
    IconLinkProfile,
    InformationText
  },
  mixins: [OnlineMixin, IsMobileMixin],
  async beforeRouteEnter(to, from, next) {
    const user = store.getters["user/user"];
    const isLoggedIn = store.getters["user/isLoggedIn"];

    if (!user?.constituencyId) {
      next({ name: "welcome" });
      return;
    }

    let toLoad = [
      store.dispatch("location/fetchCantons"),
      store.dispatch("party/fetchParties"),
      store.dispatch("council/fetchCouncils")
    ];

    if (isLoggedIn) {
      toLoad = [
        ...toLoad,
        store.dispatch("election/fetchElections"),
        store.dispatch("user/fetchProgression"),
        store.dispatch("survey/fetchSurveys")
      ];
    }
    await Promise.all(toLoad);
    // Active election must be set before questions/responses are fetched

    if (isLoggedIn) {
      await Promise.all([
        store.dispatch("questionnaire/fetchQuestions"),
        store.dispatch("questionnaire/fetchResponses"),
        store.dispatch("questionnaire/fetchSmartSpider"),
        store.dispatch("candidate/fetchCandidates"),
        store.dispatch("candidate/fetchMatches")
      ]);
    }

    let menuRoutes = [
      "/voting-instructions",
      "/our-system",
      "/get-involved",
      "/settings",
      "/credits"
    ];
    if (menuRoutes.includes(from.path)) {
      next(vm => {
        vm.showMenu = true;
      });
      return;
    }

    next();
  },
  data: () => ({
    menuToggle: false,
    canUseMenuToggle: false,
    canSwitchCouncil: false,
    canAnswerSurvey: false,
    hasAnsweredQuestions: false,
    hasFavoriteList: false,
    hasSeenDreamTeams: false,
    hasSeenResults: false,
    highlightMenuToggle: false,
    showSurvey: false,
    show: false,
    hasSeenElectionPage: false,
    showMenu: false,
    tutorialBus: new Vue(),
    levelSwitchBus: new Vue(),
    backgroundImageUrl,
    cantonalCouncils: null,
    showAvailabilityPopUp: false,
    availabilityPopUpText: "",
    availabilityPopUpTitle: "",
    availability: {
      federal_councillor: "available",
      councillor: "available",
      judge: "available"
    },
    availabilityWebsites: []
  }),
  async created() {
    this.menuToggle = !!(await this.get({ key: "homeMenuToggle" }));
    this.checkProgression();
    await store.dispatch("location/fetchConstituencies", {
      cantonId: this.user.cantonId
    });
    if (this.levelSwitchBus) {
      this.levelSwitchBus.$on("close-survey", this.closeSurvey);
      this.levelSwitchBus.$on("open-survey", this.openSurvey);
      this.levelSwitchBus.$emit("location-switch", "government-home");
    }
    client.status().then(status => {
      this.availability = Object.assign(
        {
          federal_councillor: "available",
          councillor: "available",
          judge: "available"
        },
        status.availability || {}
      );
    });
    if (this.isOnline) {
      this.loadCantonalCouncils();
    }

    if (this.isMobile) {
      store.dispatch("notifications/fetchNotifications");
      store.dispatch("notifications/requestPermission");
    }
  },
  async mounted() {
    this.setSelectedMenu("government-home");
  },
  computed: {
    ...mapGetters("user", [
      "user",
      "hasProgress",
      "contentLanguage",
      "electionContentLanguage",
      "hasSeenLanguageNotAvailableWarning"
    ]),
    ...mapGetters("location", ["canton", "constituency"]),
    ...mapGetters("survey", ["activeSurvey"]),
    ...mapGetters("list", ["lists"]),
    ...mapGetters("election", [
      "election",
      "elections",
      "activeElection",
      "hasElectionProgress",
      "hasElectionForLevel",
      "hasNewestElection",
      "hasNewCurrentElection"
    ]),
    ...mapGetters("questionnaire", ["shortAnswered", "allAnswered"]),
    ...mapGetters("council", [
      "stateCouncil",
      "nationalCouncil",
      "federalAssembly"
    ]),
    ...mapGetters("viewState", [
      "isInInteractiveMode",
      "constituencyLevel",
      "flagUrl",
      "currentLegislature"
    ]),
    offlineCantonalData() {
      return !this.isOnline && this.hasElectionForLevel("cantonal");
    },
    cantonalExecutiveCouncil() {
      return this.cantonalCouncils?.find(c => c.type === "executive");
    },
    executiveCouncilName() {
      const levels = {
        federal: this.$t("views.government-home.federal_council"),
        cantonal: this.cantonalExecutiveCouncil?.name
      };

      if (this.offlineCantonalData) {
        return this.$t("views.government-home.offline_council_name.executive");
      }

      return levels[this.constituencyLevel];
    },
    cantonalLegislativeCouncil() {
      return this.cantonalCouncils?.find(c => c.type === "legislative");
    },
    legislativeCouncilName() {
      const levels = {
        federal: this.$t("views.government-home.parlament"),
        cantonal: this.cantonalLegislativeCouncil?.name
      };

      if (this.offlineCantonalData) {
        return this.$t(
          "views.government-home.offline_council_name.legislative"
        );
      }

      return levels[this.constituencyLevel];
    },
    cantonalJudiciaryCouncil() {
      return this.cantonalCouncils?.find(c => c.type === "judiciary");
    },
    judiciaryCouncilName() {
      const levels = {
        federal: this.$t("views.government-home.federal_court"),
        cantonal: this.cantonalJudiciaryCouncil?.name
      };

      if (this.offlineCantonalData) {
        return this.$t("views.government-home.offline_council_name.judiciary");
      }

      return levels[this.constituencyLevel];
    },
    legislativeLeftChamber() {
      if (this.constituencyLevel === "federal") {
        return this.$t("views.government-home.national_council");
      }
      return undefined;
    },
    legislativeRightChamber() {
      if (this.constituencyLevel === "federal") {
        return this.$t("views.government-home.state_council");
      }
      return undefined;
    },
    levelHasGovernmentData() {
      return level =>
        level === "federal" ||
        (level === "cantonal" && this.cantonalCouncils?.length > 0);
    },
    electionNotification() {
      return (
        this.hasInterestingElection(this.constituencyLevel) ||
        (this.hasProgress(ProgressStepId.SeenAll) &&
          !this.hasProgress(ProgressStepId.LevelSwitchBack))
      );
    },
    currentLevelHasElectionData() {
      return this.hasElectionForLevel(this.constituencyLevel);
    },
    isElectionInInteractiveMode() {
      return this.isInInteractiveMode && this.currentLevelHasElectionData;
    },
    currentLevelHasGovernmentData() {
      return this.levelHasGovernmentData(this.constituencyLevel);
    },
    isGovernmentInInteractiveMode() {
      return (
        this.isInInteractiveMode &&
        (this.currentLevelHasGovernmentData ||
          (this.constituencyLevel === "cantonal" && this.offlineCantonalData))
      );
    },
    executive() {
      return parseInt(
        this.elections.find(id => this.election(id).type === "executive") || 0
      );
    },
    legislative() {
      return parseInt(
        this.elections.find(id => this.election(id).type === "legislative") || 0
      );
    },
    electionDateDisplay() {
      if (this.activeElection && this.activeElection.nextElectionDate) {
        const now = dayjs();
        const electionDate = dayjs(this.activeElection.nextElectionDate);
        const duration = electionDate.diff(now, "day");

        if (duration > 0) {
          return `${this.$tc(
            "views.home.election_on",
            this.activeElection.currentRound
          )} ${electionDate.format("DD. MMMM YYYY")}`;
        }
      }

      return "";
    },
    countdown() {
      if (!this.activeElection || !this.activeElection.nextElectionDate) {
        return null;
      }

      const MIDDAY = 12; // all elections close at 12
      let difference = dayjs();
      let electionDate = dayjs(this.activeElection.nextElectionDate).hour(
        MIDDAY
      );
      const now = dayjs();
      if (now.isAfter(electionDate)) {
        return null;
      }

      const days = electionDate.diff(difference, "day");
      difference = difference.add(days, "day");
      const hours = electionDate.diff(difference, "hour");

      difference = difference.add(hours, "hour");
      const minutes = electionDate.diff(difference, "minute");
      return { days, hours, minutes };
    },
    onGovPage() {
      return this.$route.name == "government-home";
    },
    onElectionPage() {
      return this.onElectionHomePage && !this.menuToggle;
    },
    onInfoPage() {
      return this.onElectionHomePage && this.menuToggle;
    },
    onElectionHomePage() {
      return this.$route.name == "election-home";
    },
    showWarningLanguageNotAvailable() {
      return (
        this.electionContentLanguage !== this.contentLanguage &&
        !this.hasSeenLanguageNotAvailableWarning
      );
    },
    constituencyName() {
      const levelToName = {
        federal: this.$t("views.government-home.legislature", {
          legislature: this.currentLegislature.number
        }),
        cantonal: this.canton(this.user.cantonId).name,
        communal: this.constituency(this.user.constituencyId)?.name || ""
      };
      return levelToName[this.constituencyLevel];
    }
  },
  watch: {
    activeElection() {
      this.checkProgression();
    },
    user(newVal, oldVal) {
      if (newVal && newVal.cantonId !== oldVal?.cantonId) {
        this.loadCantonalCouncils();
      }
    },
    isOnline() {
      if (this.isOnline) {
        this.loadCantonalCouncils();
      }
    }
  },
  methods: {
    ...mapActions(["store", "get"]),
    ...mapActions("user", ["updateUser", "updateProgression"]),
    ...mapMutations("viewState", ["setSelectedMenu"]),
    loadCantonalCouncils() {
      client
        .fetchCantonalCouncils(this.user.cantonId, this.contentLanguage)
        .then(cantonalCouncils => {
          this.cantonalCouncils = cantonalCouncils;
        });
    },
    hasInterestingElection(level) {
      return this.hasNewCurrentElection(level);
    },
    showNotificationLevelSwitch(level) {
      const notSameConstituency = level !== this.constituencyLevel;

      return notSameConstituency && this.hasInterestingElection(level);
    },
    onMenuClick(buttonName) {
      const menuMapping = {
        "bottom-left": {
          name: "council",
          query: {
            councilId: this.nationalCouncil.id
          }
        },
        "bottom-right": {
          name: "council",
          query: {
            councilId: this.stateCouncil.id
          }
        },
        "top-left": {
          name: "executive"
        },
        "top-right": {
          name: "judiciary"
        },
        middle: {
          name: "council",
          query: { councilId: this.federalAssembly.id }
        },
        bottom: {
          name: "election-home"
        },
        information: {
          name: "information",
          params: {
            resource: "government",
            level: this.constituencyLevel
          }
        }
      };

      if (buttonName === "information" && !this.isMobileScreen) {
        return;
      }

      if (this.constituencyLevel === "cantonal") {
        menuMapping["top-left"] = {
          name: "canton-council",
          params: {
            id: this.cantonalExecutiveCouncil?.id
          }
        };
        menuMapping["middle"] = {
          name: "canton-council",
          params: {
            id: this.cantonalLegislativeCouncil?.id
          }
        };
        menuMapping["top-right"] = {
          name: "canton-council",
          params: {
            id: this.cantonalJudiciaryCouncil?.id
          }
        };
      }

      if (buttonName in menuMapping) {
        let dataAvailability = "available";

        if (this.constituencyLevel === "federal") {
          if (buttonName === "top-left") {
            dataAvailability = this.availability.federal_councillor;
          } else if (buttonName === "top-right") {
            dataAvailability = this.availability.judge;
          } else if (
            ["middle", "bottom-left", "bottom-right"].includes(buttonName)
          ) {
            dataAvailability = this.availability.councillor;
          }
        } else if (
          this.constituencyLevel === "cantonal" &&
          this.offlineCantonalData
        ) {
          dataAvailability = "offline";
        }

        if (dataAvailability === "available") {
          this.$router.push(menuMapping[buttonName]);
        } else {
          this.availabilityWebsites = this.isOnline
            ? this.$t("views.government-home.availability.websites")
            : [];
          this.availabilityPopUpTitle = this.$t(
            `views.government-home.availability.${dataAvailability ||
              "none"}.title`
          );
          this.availabilityPopUpText = this.$t(
            `views.government-home.availability.${dataAvailability ||
              "none"}.text`
          );
          this.showAvailabilityPopUp = true;
        }
      }
    },
    toggleMenu(toggle) {
      if (toggle == this.menuToggle) {
        return;
      }
      if (this.canUseMenuToggle) {
        this.menuToggle = toggle;
        this.store({ key: "homeMenuToggle", value: this.menuToggle });
        this.checkProgression();
      }
    },
    checkProgression() {
      if (this.activeElection == null) {
        return;
      }
      let legislative = this.legislative;
      let executive = this.executive;
      let otherElection =
        this.activeElection.id != executive ? executive : legislative;
      let menuToggle = this.menuToggle;

      const hasAnsweredQuestions = this.hasProgress(
        ProgressStepId.Questionnaire
      );
      const hasAnsweredElectionQuestions = this.shortAnswered;
      this.hasSeenElectionPage =
        this.hasProgress(ProgressStepId.ElectionHome) || hasAnsweredQuestions;

      let hasMatchedElectionCandidates = this.hasElectionProgress(
        ElectionProgressStepId.Match,
        this.activeElection.id
      );

      let hasMatchedOtherElectionCandidates = this.hasElectionProgress(
        ElectionProgressStepId.Match,
        otherElection
      );

      let hasSeenDreamTeams = this.hasProgress(ProgressStepId.DreamTeams);
      let hasSeenResults = this.hasProgress(ProgressStepId.Results);
      let hasFavoriteList = this.hasProgress(ProgressStepId.FavoriteList);

      let canSwitchCouncil = hasSeenResults;

      this.hasAnsweredQuestions =
        hasAnsweredQuestions && hasAnsweredElectionQuestions;
      this.canAnswerSurvey = canSwitchCouncil || true; // TODO remove this override
      this.canSwitchCouncil = canSwitchCouncil;
      this.canUseMenuToggle = hasFavoriteList && hasMatchedElectionCandidates;
      this.hasSeenDreamTeams = hasSeenDreamTeams;
      this.hasSeenResults = hasSeenResults;
      this.hasFavoriteList = hasFavoriteList;
      this.highlightMenuToggle =
        (!menuToggle && !hasSeenResults) ||
        (menuToggle && canSwitchCouncil && !hasMatchedOtherElectionCandidates);
    },
    goToGov() {
      if (!this.onGovPage) {
        this.$router.push({ name: "government-home" });
      }
    },
    goToElection() {
      if (!this.onElectionHomePage) {
        this.$router.push({ name: "election-home" });
      }
      this.toggleMenu(false);
    },
    goToInfo() {
      if (!this.onElectionHomePage) {
        this.$router.push({ name: "election-home" });
      }
      this.toggleMenu(true);
    },
    openSurvey() {
      this.showSurvey = true;
      this.levelSwitchBus.$emit("location-switch", "survey");
    },
    closeSurvey() {
      this.showSurvey = false;
      this.levelSwitchBus.$emit("location-switch", "government-home");
    }
  }
};
</script>

<style lang="scss" scoped>
.content-scroll-container {
  margin-bottom: 3.6885rem;
}

@media (min-height: 1024px) {
  .content-scroll-container {
    margin-bottom: 4.17525rem;
  }
}

@media (max-width: 380px), (max-height: 600px) {
  .content-scroll-container {
    margin-bottom: 2.6885rem;
  }
}

.interaction-button {
  position: absolute;
  top: 1.64rem;
  left: 0.64rem;
}

.settings-button {
  position: absolute;
  top: 1.64rem;
  right: 0.64rem;
  z-index: 30;
}

@media (min-width: 1024px) {
  .interaction-button,
  .settings-button {
    top: 0;
  }

  .interaction-button {
    left: 2.16rem;
  }

  .settings-button {
    right: 2.16rem;
  }
}
</style>
